import {useContext, useEffect, useRef, useState} from 'react';
import {ClientProvider} from '@stripe-internal/conversations-ui';
import {
  STPTwilio,
  TwilioClient,
} from '@stripe-internal/twilio-conversations-client';
import {UniversalHelpClient} from '@stripe-internal/universal-help-conversations-client';
import {useInvisibleFrame} from '@stripe-internal/stripethirdparty';
import {
  ClientUrls,
  EmailIssueClientUrls,
  ObservabilityConfig,
  RequestACallClientUrls,
} from '@stripe-internal/conversations-client';
import {useInlineDataEvents} from '@stripe-internal/inline-data';
import {ConfigContext} from '../ConfigProvider';
import {UHLiveChannelsClient} from './UHLiveChannelsClient';
import {useObservability} from '../ObservabilityProvider';
import {useProfile} from '../ProfileProvider';
import {useApi} from '../ApiProvider/ApiProvider';
import {useRequestAction} from '../../useRequestAction';

const injectClientAnalyticsParams = (
  client: UHLiveChannelsClient | undefined,
  eventParameters?: Record<string, unknown>,
): Record<string, unknown> => {
  if (client === undefined) {
    return {...eventParameters};
  }
  const clientAnalyticsParameters = client.analyticsParams;
  return {
    ...clientAnalyticsParameters,
    ...eventParameters,
  };
};

interface ClientOpts {
  clientFetch?: typeof fetch;
  urls?: Partial<ClientUrls>;
  observability?: ObservabilityConfig;
}

const useTwilioClient = (
  clientOps: ClientOpts,
  stripeThirdPartyBaseUrl: string,
) => {
  const [twilioClient] = useState(() => new TwilioClient(clientOps));

  const invisibleFrame = useInvisibleFrame({
    baseURL: stripeThirdPartyBaseUrl,
    type: 'TwilioConversations',
    handleMessage: twilioClient.handleSTPEvent,
  });

  useEffect(() => {
    if (invisibleFrame?.sendRequest) {
      // @ts-expect-error -- previously we used any because we loading 2 possible STP packages
      // TODO: we need to look into this type error now
      twilioClient.initialize(new STPTwilio(invisibleFrame.sendRequest));
    }
  }, [twilioClient, invisibleFrame]);

  return twilioClient;
};

type Props = {
  children: JSX.Element;
};

export const UniversalHelpClientProvider = ({children}: Props) => {
  const {
    audience,
    entrypoint: entryPoint,
    stripeThirdPartyBaseUrl,
  } = useContext(ConfigContext);
  const {
    profile: {featureFlags},
  } = useProfile();
  const api = useApi();

  const requestAction = useRequestAction();

  // Declaring it up front so analytics has a reference to the client and its corresponding context
  const uhLiveChannelsRef = useRef<UHLiveChannelsClient | undefined>(undefined);

  const observability = useObservability((params) =>
    injectClientAnalyticsParams(uhLiveChannelsRef.current, params),
  );

  const emailUrls: EmailIssueClientUrls = {
    emailIssueUrl: '/api/contact-form',
  };

  const racUrls: RequestACallClientUrls = {
    requestACallUrl: '/api/request-a-call',
    verificationCodesUrl: `/api/request-a-call/verification-code?redirectUrl=${document.referrer}`,
    channelsUrl: `/api/eligible-channels/${entryPoint}`,
  };

  const uhUrls = {
    conversationUrl: '/api/universal-help',
    ...emailUrls,
    ...racUrls,
  };

  const twilioUrls = {
    tokenUrl: `/api/chat-token?audience=${audience}&entrypoint=${entryPoint}`,
    conversationUrl: `/api/start-chat?audience=${audience}&entrypoint=${entryPoint}`,
    ...emailUrls,
    ...racUrls,
  };

  const baseClientOpts: ClientOpts = {
    clientFetch: api.fetch,
    observability,
  };

  const twilioClientOpts: ClientOpts = {
    ...baseClientOpts,
    urls: twilioUrls,
  };

  const uhClientOpts: ClientOpts = {
    ...baseClientOpts,
    urls: uhUrls,
  };

  const uhRef = useRef<UniversalHelpClient>(
    new UniversalHelpClient(uhClientOpts),
  );
  const uhClient = uhRef.current;

  const chatClient = useTwilioClient(twilioClientOpts, stripeThirdPartyBaseUrl);

  const inlineDataEvents = useInlineDataEvents();

  uhLiveChannelsRef.current = new UHLiveChannelsClient({
    ...uhClientOpts,
    featureFlags,
    api,
    uhClient,
    chatClient,
    inlineDataEventBus: inlineDataEvents,
    requestAction,
  });
  const uhLiveChannelsClient = uhLiveChannelsRef.current;

  return (
    <ClientProvider value={uhLiveChannelsClient}>{children}</ClientProvider>
  );
};
